import React from 'react';
import { Box, Typography } from '@mui/material';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function weekdayShort(dateStr) {
    return DOW[new Date(dateStr + 'T00:00:00').getDay()];
}
function shortDate(dateStr) {
    const d = new Date(dateStr + 'T00:00:00');
    return `${d.getMonth() + 1}/${d.getDate()}`;
}

/**
 * Мультивыбор дней недели для attendance-кода (absence / Night work).
 * Ряд из 7 чипов Mon..Sun — тап переключает день. Тот же визуальный язык, что у
 * чипов дней в SignDayNarrativesSection.
 *
 * Отмеченный день = код на нём СТОИТ. Снять можно ЛЮБОЙ день, включая текущий:
 * раньше текущий был «якорем» и не снимался — форман ставил ночь на Пн–Ср, потом
 * переходил на среду и не мог её отменить (чип не гас), а через селект «None»
 * код слетал сразу со всей тройки. Текущий день здесь — обычный член набора,
 * а редактор ячейки держит селект кода в синхроне с его чипом.
 *
 * Цвет активного дня — по типу кода: ночь индиго (#312e81), absence красный (#dc2626),
 * как бейджи в гриде и PDF.
 *
 * Props:
 *   - weekDates: string[] — 7 ISO-дат недели (Mon..Sun)
 *   - value: string[]     — выбранные даты
 *   - onChange: (dates: string[]) => void
 *   - night: boolean      — раскраска под Night work
 *   - currentDate: string — текущий день (подсвечиваем меткой TODAY)
 *   - markedDates: string[] — дни, где стоит какой-либо код (метка SET)
 */
export default function AttendDaysPicker({
    weekDates = [],
    value = [],
    onChange,
    night = false,
    currentDate,
    markedDates = [],
}) {
    const activeColor = night ? '#312e81' : '#dc2626';
    const selected = new Set(value);
    const marked = new Set(markedDates);

    const toggle = (d) => {
        const next = new Set(selected);
        if (next.has(d)) next.delete(d);
        else next.add(d);
        onChange([...next].sort());
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                Days with this code — tap to add or remove
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {weekDates.map((d) => {
                    const isSel = selected.has(d);
                    const isToday = d === currentDate;
                    return (
                        <Box
                            key={d}
                            component="button"
                            type="button"
                            onClick={() => toggle(d)}
                            sx={{
                                flex: '1 1 0',
                                minWidth: 44,
                                px: 0.5,
                                py: 0.6,
                                border: '1px solid',
                                borderColor: isSel ? activeColor : '#d1d5db',
                                borderRadius: '6px',
                                bgcolor: isSel ? activeColor : '#fff',
                                color: isSel ? '#fff' : 'text.secondary',
                                // Текущий день выделен рамкой, но снимается как любой другой.
                                boxShadow: isToday ? '0 0 0 2px #94a3b8' : 'none',
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                                lineHeight: 1.15,
                                WebkitTapHighlightColor: 'transparent',
                                '&:active': { transform: 'scale(0.96)' },
                            }}
                        >
                            <Box sx={{ fontSize: '0.7rem', fontWeight: 700 }}>{weekdayShort(d)}</Box>
                            <Box sx={{ fontSize: '0.62rem', opacity: 0.85 }}>{shortDate(d)}</Box>
                            {(isToday || marked.has(d)) && (
                                <Box sx={{ fontSize: '0.5rem', fontWeight: 700, letterSpacing: '0.03em' }}>
                                    {isToday ? 'TODAY' : 'SET'}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
            {night && (
                <Typography variant="caption" sx={{ display: 'block', color: '#4338ca', mt: 0.5 }}>
                    Days with no hours yet stay unmarked — the night code is applied when you enter hours.
                </Typography>
            )}
        </Box>
    );
}
